<template>
    <div>
        <div class="tab-wrapper">
            <nav class="tab-nav">
                <button
                    v-for="tab in tabs"
                    :key="tab.id"
                    :class="['tab-item', { 'tab-item--active': activeTabId === tab.id }]"
                    @click="setActiveTab(tab)"
                    type="button"
                >
                    <i v-if="tab.icon" :class="['tab-icon', tab.icon]"></i>
                    {{ tab.title }}
                    <span v-if="tab.count !== undefined" class="tab-badge">{{ tab.count }}</span>
                </button>
            </nav>
            <div class="tab-indicator-bar">
                <div class="tab-indicator" :style="indicatorStyle" />
            </div>
        </div>

        <div class="tab-content mt-1">
            <Transition name="tab-fade" mode="out-in">
                <component
                    :is="activeComponent"
                    :key="activeTabId"
                />
            </Transition>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import Appointment from '@/components/molecule/company/dental-os/single-service/appointment/Appointment.vue';
import Patients from '@/components/molecule/company/dental-os/single-service/patients/Patients.vue';
import Reports from '@/components/molecule/company/dental-os/single-service/reports/Reports.vue';
import useDate from "@/services/utils/day";

const route  = useRoute();
const router = useRouter();
const {currentDate} = useDate();

const tabs = [
    {
        id: 'appointment',
        title: 'Appointment',
        icon: 'fas fa-calendar-check',
        component: Appointment,
        props: {},
    },
    {
        id: 'patients',
        title: 'Patients',
        icon: 'fas fa-users',
        component: Patients,
        props: {},
    },
    {
        id: 'reports',
        title: 'Reports',
        icon: 'fas fa-file-medical',
        component: Reports,
        props: {},
    },
];

const DEFAULT_TAB = tabs[0].id;

const activeTabId = ref(route.query.tab || DEFAULT_TAB);

watch(
    () => route.query.tab,
    (newTab) => {
        const valid = tabs.find(t => t.id === newTab);
        activeTabId.value = valid ? newTab : DEFAULT_TAB;
    },
    { immediate: true }
);

const activeTabDef = computed(
    () => tabs.find(t => t.id === activeTabId.value) ?? tabs[0]
);

const activeComponent = computed(() => activeTabDef.value.component);

const indicatorStyle = computed(() => {
    const index = tabs.findIndex(t => t.id === activeTabId.value);
    const pct   = 100 / tabs.length;
    return {
        width:     `${pct}%`,
        transform: `translateX(${index * 100}%)`,
    };
});

const setActiveTab = (tab) => {
    if (activeTabId.value === tab.id) return;
    router.push({
        query: {
            start: currentDate(),
            end: currentDate(),
            tab: tab.id,
            page: 1
        }
    });
};

onMounted(() => {
    if (!route.query.tab) {
        router.replace({ query: {
            start: currentDate(),
            end: currentDate(),
            tab: DEFAULT_TAB,
            page: 1
        } });
    }
});
</script>

<style scoped>

.tab-nav {
    display: flex;
    align-items: stretch;
}
.tab-item {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 0.55rem 0.75rem;
    border: none;
    background: transparent;
    color: #6b7280;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.18s ease, color 0.18s ease;
    white-space: nowrap;
    position: relative;
}

.tab-item:hover:not(.tab-item--active) {
    background: #eef0f5;
    color: #374151;
}

.tab-item--active {
    background: #f1f0ff;
    color: #4f46e5;
    font-weight: 600;
}

.tab-icon {
    opacity: 0.85;
}

.tab-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 999px;
    background: #e0e7ff;
    color: #4f46e5;
    font-weight: 700;
    line-height: 1;
}

.tab-item--active .tab-badge {
    background: #4f46e5;
    color: #ffffff;
}

/* ── Sliding indicator ── */
.tab-indicator-bar {
    position: relative;
    height: 2px;
    background: #e9ecef;
    margin-top: -1px;
}

.tab-indicator {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background: #4f46e5;
    border-radius: 2px;
    transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1),
                width    0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

/* ── Tab content fade+slide transition ── */
.tab-fade-enter-active,
.tab-fade-leave-active {
    transition: opacity 0.18s ease, transform 0.18s ease;
}

.tab-fade-enter-from {
    opacity: 0;
    transform: translateY(6px);
}

.tab-fade-leave-to {
    opacity: 0;
    transform: translateY(-4px);
}
</style>